import { View } from 'foundations-components/components/View';
import { Tile } from 'foundations-components/transitional/components/Tile';
import { useCallback, useContext, useState } from 'react';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';
import useCloneTracker from '../../../../hooks/useCloneTracker';
import { STANDARD_FIELD_TYPES } from 'reporting-ui-components/lib/report-strategies/shared/constants/fieldTypes';
import { getStandardFieldType } from 'reporting-ui-components/lib/report-strategies/shared/utils/reportGetters';
import { isReportValidForReportGetterStrategy } from 'reporting-ui-components/lib/report-strategies/utils/strategyFetchers';
import ReportingAIAssistantInsights from '../../fullPage/report-ai-assistant-insights/ReportingAIAssistantInsights';
import useFetchIsAIEnabledFromSettings from '../../fullPage/report-ai-assistant-insights/useFetchIsAIEnabledFromSettings';
import { getIsInsightsShown, getReportDatasetHasNotEnoughDataForAIInsights } from '../../fullPage/report-ai-assistant-insights/utils';
import AIReportSummaryFooterWithActions from './AIReportSummaryFooterWithActions';
import { AIReportSummaryHeader } from './AIReportSummaryHeader';
import ReportInsightsContent from './ReportInsightsContent';
import ReportPresetPills from './report-presets/ReportPresetPills';
import useFetchGenerateReportRemixes from './report-presets/ai-report-remix/useFetchGenerateReportRemixes';
import { getShouldShowAIReportPresets, getShouldShowAIReportSummary } from './utils';
import { useFetchReportAIAssistantInsightsWithV2 } from '../../fullPage/report-ai-assistant-insights/useFetchReportAIAssistantInsights';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { createElement as _createElement } from "react";
const AIReportSummaryWithReportPresetsBody = ({
  report,
  appUsageTracker,
  reportResolve,
  forceRender,
  defaultOpen = false,
  onChange,
  isReportRemixChange,
  hideReportRemix
}) => {
  const usageTracker = useCloneTracker(appUsageTracker);
  const userInfo = useContext(UserInfoContext);
  const [isSummaryExpanded, setIsSummaryExpanded] = useState(defaultOpen);
  const reportDatasetIsUndefinedOrHasFewerThan1DataPoint = getReportDatasetHasNotEnoughDataForAIInsights(report, reportResolve);
  const isAIEnabled = useFetchIsAIEnabledFromSettings();
  const useInsightFetchHook = useFetchReportAIAssistantInsightsWithV2(report);
  const reportInsightsFetch = useInsightFetchHook({
    report,
    reportResolve,
    isDeferred: !reportResolve || !!reportDatasetIsUndefinedOrHasFewerThan1DataPoint || !isSummaryExpanded || !isAIEnabled,
    numberOfInsights: 3,
    enableSameReportCaching: true
  });
  const aiReportPresetFetch = useFetchGenerateReportRemixes({
    report,
    deferred: !getShouldShowAIReportPresets({
      report,
      userInfo
    }) || !!isReportRemixChange || !isSummaryExpanded || !isAIEnabled || hideReportRemix
  });
  const onExpandSummary = useCallback(isExpanded => {
    setIsSummaryExpanded(isExpanded);
    if (isReportValidForReportGetterStrategy(report)) {
      usageTracker.track('reportingAIAssistantInsightsInteraction', {
        action: 'openSummary',
        isOpen: isExpanded,
        chartType: getStandardFieldType(report, STANDARD_FIELD_TYPES.CHART_TYPE)
      });
    }
  }, [report, usageTracker]);
  if (!isSummaryExpanded) {
    return /*#__PURE__*/_jsx(Tile, {
      padding: "600",
      children: /*#__PURE__*/_jsx(AIReportSummaryHeader, {
        isSummaryExpanded: isSummaryExpanded,
        setIsSummaryExpanded: onExpandSummary,
        appUsageTracker: appUsageTracker,
        report: report,
        reportResolve: reportResolve,
        onReportChange: onChange
      })
    });
  }
  return /*#__PURE__*/_jsxs(Tile, {
    padding: "600",
    children: [/*#__PURE__*/_jsx(AIReportSummaryHeader, {
      isSummaryExpanded: isSummaryExpanded,
      setIsSummaryExpanded: onExpandSummary,
      appUsageTracker: appUsageTracker,
      report: report,
      reportResolve: reportResolve,
      onReportChange: onChange
    }), /*#__PURE__*/_jsx(ReportInsightsContent, {
      appUsageTracker: appUsageTracker,
      report: report,
      reportResolve: reportResolve,
      insightsFetch: reportInsightsFetch
    }), !hideReportRemix && /*#__PURE__*/_jsx(View, {
      marginBlockStart: "400",
      children: /*#__PURE__*/_jsx(ReportPresetPills, {
        appUsageTracker: appUsageTracker,
        report: report,
        isReportRemixChange: isReportRemixChange,
        onChange: onChange,
        aiReportPresetFetch: aiReportPresetFetch
      })
    }), /*#__PURE__*/_jsx(AIReportSummaryFooterWithActions, {
      forceRender: forceRender,
      appUsageTracker: appUsageTracker,
      setIsSummaryExpanded: onExpandSummary
    })]
  });
};
const AIReportSummaryWithReportPresets = props => {
  const {
    showAIBuilder = false,
    appUsageTracker,
    report,
    reportResolve
  } = props;
  const [isDefaultOpen, setIsDefaultOpen] = useState(false);
  const [index, setIndex] = useState(0);
  const userInfo = useContext(UserInfoContext);
  if (!showAIBuilder && getShouldShowAIReportSummary({
    report,
    userInfo
  })) {
    return /*#__PURE__*/_jsx(View, {
      children: /*#__PURE__*/_createElement(AIReportSummaryWithReportPresetsBody, Object.assign({}, props, {
        forceRender: () => {
          setIsDefaultOpen(true);
          setIndex(currIndex => currIndex + 1);
        },
        defaultOpen: isDefaultOpen,
        key: index
      }))
    });
  } else if (!showAIBuilder && getIsInsightsShown(report, userInfo)) {
    return /*#__PURE__*/_jsx(ReportingAIAssistantInsights, {
      appUsageTracker: appUsageTracker,
      report: report,
      reportResolve: reportResolve,
      showAIBuilder: showAIBuilder
    });
  }
  return null;
};
export default AIReportSummaryWithReportPresets;